import CryptoJS from 'crypto-js'
import { isEmpty } from 'lodash'

import api from '@/api'
import { KEY_BARGE_LINE_UP_DETAIL_ID } from '@/fixtures/Constants'
import { CRYPTO_KEY } from '@/fixtures/Hash'
import { convertFileSize } from '@/utils/fileUtils'

const encBargeLineUpKey = btoa(KEY_BARGE_LINE_UP_DETAIL_ID)

const namespaced = true

const state = {
  barges: [],
  bargeId: localStorage.getItem(encBargeLineUpKey) || null,
  bargeDetail: null,
  bargeDelayTime: null,
  latestBargeDelayTime: null,
  bargeItems: [],
  tugItems: [],
  termItems: [],
  vesselItems: [],
  thirdPartyItems: null,
  productIdItems: [],
  polItems: [],
  podItems: [],
  routeItems: [],
  productItems: [],
  kpiItems: [],
  latestPol: null,
  latestPod: null,
  bargeLogs: [],
  editableBarges: [],
  bargeLineUpsFiles: {},
  bargeEditOptions: [],
  tugEditOptions: [],
  companiesOptions: [],
  previewUploadBLU: [],
  dropdownOptionsBLU: [],
  detailPlan: null,
  calculatedSimulation: null,
  bargeLineUpUploadColumnConfig: { column_start: null, column_end: null }
}

const getters = {
  barges: state => state.barges,
  bargeId: state => state.bargeId
    ? Number(CryptoJS.AES.decrypt(state.bargeId, CRYPTO_KEY).toString(CryptoJS.enc.Utf8))
    : null,
  bargeDetail: state => state.bargeDetail,
  bargeDelayTime: state => state.bargeDelayTime,
  latestBargeDelayTime: state => state.latestBargeDelayTime,
  bargeItems: state => state.bargeItems,
  tugItems: state => state.tugItems,
  termItems: state => state.termItems,
  vesselItems: state => state.vesselItems,
  thirdPartyItems: state => state.thirdPartyItems,
  productIdItems: state => state.productIdItems,
  polItems: state => state.polItems,
  podItems: state => state.podItems,
  routeItems: state => state.routeItems,
  productItems: state => state.productItems,
  kpiItems: state => state.kpiItems,
  latestPol: state => state.latestPol,
  latestPod: state => state.latestPod,
  bargeLogs: state => state.bargeLogs,
  editableBarges: state => state.editableBarges,
  bargeLineUpsFiles: state => state.bargeLineUpsFiles,
  bargeEditOptions: state => state.bargeEditOptions,
  tugEditOptions: state => state.tugEditOptions,
  companiesOptions: state => state.companiesOptions,
  previewUploadBLU: state => state.previewUploadBLU,
  dropdownOptionsBLU: state => state.dropdownOptionsBLU,
  detailPlan: state => state.detailPlan,
  calculatedSimulation: state => state.calculatedSimulation,
  bargeLineUpUploadColumnConfig: state => state.bargeLineUpUploadColumnConfig
}

const mutations = {
  SET_BARGES: (state, payload) => {
    state.barges = payload
  },
  SET_BARGE_ID: (state, payload) => {
    state.bargeId = payload
    localStorage.setItem(encBargeLineUpKey, payload)
  },
  SET_BARGE_DETAIL: (state, payload) => {
    state.bargeDetail = payload
  },
  SET_BARGE_DELAY_TIME: (state, payload) => {
    state.bargeDelayTime = payload
  },
  SET_LATEST_BARGE_DELAY_TIME: (state, payload) => {
    state.latestBargeDelayTime = payload
  },
  SET_BARGE_ITEMS: (state, payload) => {
    state.bargeItems = payload
  },
  SET_TUG_ITEMS: (state, payload) => {
    state.tugItems = payload
  },
  SET_TERM_ITEMS: (state, payload) => {
    state.termItems = payload
  },
  SET_VESSEL_ITEMS: (state, payload) => {
    state.vesselItems = payload
  },
  SET_THIRD_PARTY_ITEMS: (state, payload) => {
    state.thirdPartyItems = payload
  },
  SET_PRODUCT_ID_ITEMS: (state, payload) => {
    state.productIdItems = payload
  },
  SET_POL_ITEMS: (state, payload) => {
    state.polItems = payload
  },
  SET_POD_ITEMS: (state, payload) => {
    state.podItems = payload
  },
  SET_ROUTE_ITEMS: (state, payload) => {
    state.routeItems = payload
  },
  SET_PRODUCT_ITEMS: (state, payload) => {
    state.productItems = payload
  },
  SET_KPI_ITEMS: (state, payload) => {
    state.kpiItems = payload
  },
  SET_LATEST_POL: (state, payload) => {
    state.latestPol = payload
  },
  SET_LATEST_POD: (state, payload) => {
    state.latestPod = payload
  },
  SET_BARGE_LOGS: (state, payload) => {
    state.bargeLogs = payload
  },
  SET_EDITABLE_BARGES: (state, payload) => {
    state.editableBarges = payload
  },
  SET_BARGE_LINE_UPS_FILES: (state, payload) => {
    state.bargeLineUpsFiles = payload
  },
  SET_BARGE_EDIT_OPTIONS: (state, payload) => {
    state.bargeEditOptions = payload
  },
  SET_TUG_EDIT_OPTIONS: (state, payload) => {
    state.tugEditOptions = payload
  },
  SET_COMPANIES_OPTIONS: (state, payload) => {
    state.companiesOptions = payload
  },
  SET_PREVIEW_UPLOAD_BLU: (state, payload) => {
    state.previewUploadBLU = payload
  },
  SET_DROPDOWN_OPTIONS_BLU: (state, payload) => {
    state.dropdownOptionsBLU = payload
  },
  SET_DETAIL_PLAN: (state, payload) => {
    state.detailPlan = payload
  },
  SET_CALCULATED_SIMULATION: (state, payload) => {
    state.calculatedSimulation = payload
  },
  SET_BARGE_LINE_UP_UPLOAD_COLUMN_CONFIG: (state, payload) => {
    state.bargeLineUpUploadColumnConfig = payload
  }
}

const actions = {
  async getBarges({ commit, rootGetters }, filterPayload) {
    try {
      const filterConfig = rootGetters['Table/defaultConfig']

      const filter = {
        ...filterPayload,
        search: filterConfig.search,
        page: filterConfig.currentPage,
        limit: filterConfig.pageSize
      }

      const { data } = await api.getBargeLineUp(filter)
      if (data.error) {
        commit('SET_BARGES', [])
        commit('Table/SET_TOTAL_DATA', 0, { root: true })
        commit('Table/SET_TOTAL_PAGE', 1, { root: true })
        return
      }

      commit('SET_BARGES', data.data)
      commit('Table/SET_TOTAL_DATA', data.total, { root: true })
      commit('Table/SET_TOTAL_PAGE', data.pages, { root: true })
    } catch (err) {
      throw err
    }
  },
  async getBargeDetail({ commit, getters }, payload) {
    try {
      const { data } = await api.getBargeLineUpDetail(payload.id, payload.is_blu_return_cargo)

      commit('SET_BARGE_ID', CryptoJS.AES.encrypt(payload.toString(), CRYPTO_KEY).toString())
      if (data.error) {
        commit('SET_BARGE_DETAIL', null)
        return
      }

      commit('SET_BARGE_DETAIL', data)
    } catch (err) {
      throw err
    }
  },
  async postBargeLineUp({}, payload) {
    try {
      await api.postBargeLineUp(payload)
    } catch (err) {
      throw err
    }
  },
  async putBargeLineUp({ commit }, payload) {
    try {
      const id = payload.id
      delete payload.id

      const { data } = await api.putBargeLineUp(id, payload)

      if (data.error) {
        commit('SET_EDITABLE_BARGES', [])
        return
      }

      commit('SET_EDITABLE_BARGES', data)
    } catch (err) {
      throw err
    }
  },
  async putBargeLineUpActual({}, payload) {
    try {
      const id = payload.id
      delete payload.id

      await api.putBargeLineUpActual(id, payload)
    } catch (err) {
      throw err
    }
  },
  async uploadDelayTime({ commit, dispatch }, payload) {
    try {
      const formData = new FormData()
      formData.append('delay_time_file', payload)

      // add start progress to change component state immediately
      commit('UploadFile/SET_PROGRESS', 0.25, { root: true })
      const { data } = await api.uploadDelayTime(formData, (progress) => {
        const totalLength = progress.lengthComputable
          ? progress.total
          : progress.target.getResponseHeader('content-length')
          || progress.target.getResponseHeader('x-decompressed-content-length')

        commit('UploadFile/SET_PROGRESS', totalLength
          ? Math.round((progress.loaded * 100) / totalLength)
          : 100,
        { root: true })
      })

      if (data.error) {
        commit('UploadFile/SET_PROGRESS', 0, { root: true })
        commit('UploadFile/SET_IS_UPLOAD_ERROR', true, { root: true })
        return
      }

      commit('UploadFile/SET_FILE_NAME', data.filename, { root: true })
      commit('UploadFile/SET_FILE_SIZE', convertFileSize(payload.size), { root: true })
      commit('UploadFile/SET_IS_UPLOAD_ERROR', false, { root: true })
      commit('SET_BARGE_DELAY_TIME', data.data.length ? data.data : null)
    } catch (err) {
      commit('UploadFile/SET_PROGRESS', 0, { root: true })
      throw err
    }
  },
  async postDelayTime({ commit }, payload) {
    try {
      await api.postDelayTime(payload)
    } catch (err) {
      throw err
    }
  },
  async getLatestDelayTime({ commit }, payload) {
    try {
      const { data } = await api.getLatestDelayTime(payload)

      if (data.error) {
        commit('SET_LATEST_BARGE_DELAY_TIME', null)
        return
      }

      commit('SET_LATEST_BARGE_DELAY_TIME', data)
    } catch (err) {
      throw err
    }
  },
  async getBargeItems({ commit }, payload) {
    try {
      const filter = { search: payload }
      const { data } = await api.getBargeDropdown(filter)

      if (data.error) {
        commit('SET_BARGE_ITEMS', [])
        return
      }

      commit('SET_BARGE_ITEMS', data)
    } catch (err) {
      throw err
    }
  },
  async getDropdownOptionsBLU({ commit }, payload) {
    try {
      const { data } = await api.getDropdownOptionsBLU(payload)

      if(payload && (payload.type === 'POL' || payload.type === 'POD')) {
        if (data.error) {
          commit(`SET_${payload.type.toUpperCase()}_ITEMS`, [])
          return
        }
  
        commit(`SET_${payload.type.toUpperCase()}_ITEMS`, data)
      } else {

        if (data.error) {
          commit('SET_DROPDOWN_OPTIONS_BLU', [])
          return
        }

        commit('SET_DROPDOWN_OPTIONS_BLU', data)
      }
    } catch (err) {
      throw err
    }
  },
  async getTugItems({ commit }, payload) {
    try {
      const filter = { search: payload }
      const { data } = await api.getTugDropdown(filter)

      if (data.error) {
        commit('SET_TUG_ITEMS', [])
        return
      }

      commit('SET_TUG_ITEMS', data)
    } catch (err) {
      throw err
    }
  },
  async getTermItems({ commit }, payload) {
    try {
      const filter = { search: payload }
      const { data } = await api.getTermDropdown(filter)

      if (data.error) {
        commit('SET_TERM_ITEMS', [])
        return
      }

      commit('SET_TERM_ITEMS', data)
    } catch (err) {
      throw err
    }
  },
  async getVesselItems({ commit }, payload) {
    try {
      const filter = { search: payload }
      const { data } = await api.getVesselDropdown(filter)

      if (data.error) {
        commit('SET_VESSEL_ITEMS', [])
        return
      }

      commit('SET_VESSEL_ITEMS', data)
    } catch (err) {
      throw err
    }
  },
  async getThirdPartyItems({ commit }, payload) {
    try {
      const filter = { search: payload.search }
      const { data } = await api.getThirdPartyDropdown(payload.id, filter)

      if (data.error) {
        commit('SET_THIRD_PARTY_ITEMS', null)
        return
      }

      commit('SET_THIRD_PARTY_ITEMS', data)
    } catch (err) {
      throw err
    }
  },
  async getProductIdItems({ commit }, payload) {
    try {
      const filter = { search: payload.search }
      const { data } = await api.getProductIdDropdown(payload.id, filter)

      if (data.error) {
        commit('SET_PRODUCT_ID_ITEMS', [])
        return
      }

      commit('SET_PRODUCT_ID_ITEMS', data)
    } catch (err) {
      throw err
    }
  },
  async getPolItems({ commit }, payload) {
    try {
      const filter = { search: payload }
      const { data } = await api.getPolDropdown(filter)

      if (data.error) {
        commit('SET_POL_ITEMS', [])
        return
      }

      commit('SET_POL_ITEMS', data.data)
    } catch (err) {
      throw err
    }
  },
  async getPodItems({ commit }, payload) {
    try {
      const filter = { search: payload }
      const { data } = await api.getPodDropdown(filter)

      if (data.error) {
        commit('SET_POD_ITEMS', [])
        return
      }

      commit('SET_POD_ITEMS', data.data)
    } catch (err) {
      throw err
    }
  },
  async getRouteItems({ commit }, payload) {
    try {
      const filter = { search: payload }
      const { data } = await api.getRouteDropdown(filter)

      if (data.error) {
        commit('SET_ROUTE_ITEMS', [])
        return
      }

      commit('SET_ROUTE_ITEMS', data)
    } catch (err) {
      throw err
    }
  },
  async getProductItems({ commit }, payload) {
    try {
      const filter = { search: payload }
      const { data } = await api.getProductDropdown(filter)

      if (data.error) {
        commit('SET_PRODUCT_ITEMS', [])
        return
      }

      commit('SET_PRODUCT_ITEMS', data)
    } catch (err) {
      throw err
    }
  },
  async getKpiItems({ commit }, payload) {
    try {
      const { data } = await api.getKpiDropdown(payload)
      if (data.error) {
        commit('SET_KPI_ITEMS', [])
        return
      }

      commit('SET_KPI_ITEMS', data)
    } catch (err) {
      throw err
    }
  },
  async getLatestPol({ commit }, payload) {
    try {
      const filter = { pol: payload }
      const { data } = await api.getLatestPol(filter)

      if (data.error) {
        commit('SET_LATEST_POL', null)
        return
      }

      commit('SET_LATEST_POL', data)
    } catch (err) {
      throw err
    }
  },
  async getLatestPod({ commit }, payload) {
    try {
      const filter = { pod: payload.podName, name_of_vessel: payload.vesselName }
      const { data } = await api.getLatestPod(filter)

      if (data.error) {
        commit('SET_LATEST_POD', null)
        return
      }

      commit('SET_LATEST_POD', data)
    } catch (err) {
      throw err
    }
  },
  async getBargeLog({ commit }, payload) {
    try {
      const bargeId = payload.id
      delete payload.id

      const { data } = await api.getBargeLineUpLog(bargeId, payload)

      if (data.error) {
        commit('SET_BARGE_LOGS', [])
        return
      }

      commit('SET_BARGE_LOGS', data)
    } catch (err) {
      throw err
    }
  },
  async postPolPodDropdown(_, payload) {
    try {
      await api.postPolPodDropdown(payload)
    } catch (err) {
      throw err
    }
  },
  async downloadBargeLineUps({ commit, rootGetters }, payload) {
    try {
      const filterConfig = rootGetters['Table/defaultConfig']
      const filter = {
        ...payload,
        search: filterConfig.search
      }

      const response = await api.downloadBargeLineUps(filter)
      if (response) {
        commit('SET_BARGE_LINE_UPS_FILES', response)
      }
    } catch (err) {
      throw err
    }
  },
  resetBargeLineUpsFiles({ commit }) {
    commit('SET_BARGE_LINE_UPS_FILES', {})
  },
  async fetchBluHistories(_, payload) {
    try {
      const { data } = await api.getBargeLineUpHistories(payload.id, { is_blu_return_cargo: payload.is_blu_return_cargo})
      if (data?.error) {
        return { error: data?.error }
      }
      return data
    } catch (err) {
      throw err
    }
  },
  async getBargeOptions({ commit }, payload) {
    try {
      const { data } = await api.getBargeDropdown(payload)

      if (data.error) {
        commit('SET_BARGE_EDIT_OPTIONS', [])
        return
      }

      commit('SET_BARGE_EDIT_OPTIONS', data)
    } catch (err) {
      throw err
    }
  },
  async getTugOptions({ commit }, payload) {
    try {
      const { data } = await api.getTugDropdown(payload)

      if (data.error) {
        commit('SET_TUG_EDIT_OPTIONS', [])
        return
      }

      commit('SET_TUG_EDIT_OPTIONS', data)
    } catch (err) {
      throw err
    }
  },
  async getCompaniesOptions({ commit }, payload) {
    try{
      const { data } = await api.getCompanies(payload)
      if (data.error) {
        commit('SET_COMPANIES_OPTIONS', [])
        return
      }
      commit('SET_COMPANIES_OPTIONS', data)
    }catch (err){
      throw err
    }
  },
  async getBargeLineUpUploadColumnConfig({ commit }) {
    try {
      const { data} = await api.getBargeLineUpUploadColumnConfig()
      if (data.error) {
        commit('SET_BARGE_LINE_UP_UPLOAD_COLUMN_CONFIG', { column_start: null, column_end: null })
        return
      }
      commit('SET_BARGE_LINE_UP_UPLOAD_COLUMN_CONFIG', data)
    } catch (err) {
      commit('SET_BARGE_LINE_UP_UPLOAD_COLUMN_CONFIG', { column_start: null, column_end: null })
      throw err
    }
  },
  async uploadBargeLineUp({ commit, rootGetters }, payload) {
    try {
      const formData = new FormData()
      formData.append('file', payload)

      // add start progress to change component state immediately
      commit('UploadFile/SET_PROGRESS', 0.25, { root: true })
      const { data } = await api.postBargeLineUpUpload(formData, (progress) => {
        const totalLength = progress.lengthComputable
          ? progress.total
          : progress.target.getResponseHeader('content-length')
          || progress.target.getResponseHeader('x-decompressed-content-length')

        commit('UploadFile/SET_PROGRESS', totalLength
          ? Math.round((progress.loaded * 100) / totalLength)
          : 100,
        { root: true })
      })

      if (isEmpty(data?.data)) {
        commit('UploadFile/SET_PROGRESS', 0, { root: true })
        commit('UploadFile/SET_IS_UPLOAD_ERROR', true, { root: true })
        return
      }

      const pageSize = rootGetters['Table/pageSize']
      commit('Table/SET_TOTAL_DATA', data.data.length, { root: true })
      commit('Table/SET_TOTAL_PAGE', Math.ceil(data.data.length / pageSize), { root: true })
      commit('UploadFile/SET_FILE_NAME', payload.name, { root: true })
      commit('UploadFile/SET_FILE_SIZE', convertFileSize(payload.size), { root: true })
      commit('UploadFile/SET_IS_UPLOAD_ERROR', false, { root: true })
      commit('SET_PREVIEW_UPLOAD_BLU', data.data.length ? data : [])
    } catch (err) {
      commit('UploadFile/SET_PROGRESS', 0, { root: true })
      throw err
    }
  },
  async postBargeLineUpConfig({}, payload) {
    try {
      await api.postBargeLineUpConfig(payload)
    } catch (err) {
      throw err
    }
  },
  async putBulkBargeLineUp({}, payload){
    try {
      await api.putBulkBargeLineUp(payload)
    } catch(err) {
      throw err
    }
  },
  async putBulkAssignBargeLineUp({}, payload) {
    try{
      await api.putBulkAssignBlu(payload)
    } catch (err) {
      throw err
    }
  },
  async deleteBulkDeleteBargeLineUp({}, payload) {
    try{
      await api.deleteBulkDeleteBlu(payload)
    } catch (err) {
      throw err
    }
  },
  async postValidateRow({}, payload) {
    try {
      const { data } = await api.postBargeLineUpValidateRow(payload)
      return data
    } catch (err) {
      throw err
    }
  },
  async postGenerateBLU({}, payload) {
    try {
      const { data } = await api.postBargeLineUpGenerate(payload)
      return data
    } catch (err) {
      throw err
    }
  },
  async getBargeDetailPlan({ commit }, payload) {
    try {
      const { data } = await api.getBargeLineUpDetailPlan(payload)

      if (!data) {
        commit('SET_DETAIL_PLAN', null)
        return
      }

      commit('SET_DETAIL_PLAN', data)
    } catch (err) {
      throw err
    }
  },
  async postCalculatedSimulation({ commit }, payload) {
    try {
      const { data } = await api.postCalculatedSimulation(payload.id, payload.is_blu_return_cargo, payload.data)
      if(!data) {
        commit('SET_CALCULATED_SIMULATION', null)
        return
      }
      commit('SET_CALCULATED_SIMULATION', data)
    } catch (err) {
      throw err
    }
  },
  async postReturnCargo({ commit }, payload) {
    try {
      await api.postReturnCargo(payload.id, payload.form)
    } catch (err) {
      throw err
    }
  },
  async postSendEmail({}, _) {
    try {
      await api.postSendEmailBlu()
    } catch (err) {
      throw err
    }
  }
}

export default { namespaced, state, getters, mutations, actions }
